import { initializeApp, getApps, type FirebaseOptions } from 'firebase/app';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  reload,
  updateProfile,
} from 'firebase/auth';
import { AuthFlowError, type BrowserAuthProvider } from './provider';

// every Firebase failure becomes an AuthFlowError; the shared catalogue turns its code into words
const wrap = async <T>(work: () => Promise<T>): Promise<T> => {
  try {
    return await work();
  } catch (error) {
    const code =
      typeof error === 'object' && error && 'code' in error
        ? String((error as { code: unknown }).code)
        : 'auth/unknown';
    throw new AuthFlowError(code);
  }
};

export function createFirebaseBrowserAuth(options: FirebaseOptions): BrowserAuthProvider {
  const app = getApps()[0] ?? initializeApp(options);
  const auth = getAuth(app);
  const google = new GoogleAuthProvider();
  google.setCustomParameters({ prompt: 'select_account' });
  return {
    signInWithGoogle: () => wrap(async () => (await signInWithPopup(auth, google)).user.getIdToken()),
    signInWithEmail: (email, password) =>
      wrap(async () => (await signInWithEmailAndPassword(auth, email, password)).user.getIdToken()),
    signUpWithEmail: (email, password, displayName) =>
      wrap(async () => {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(result.user, { displayName });
        await sendEmailVerification(result.user);
        return result.user.getIdToken(true);
      }),
    sendPasswordReset: (email) => wrap(() => sendPasswordResetEmail(auth, email)),
    sendVerificationEmail: () =>
      wrap(async () => {
        if (auth.currentUser) await sendEmailVerification(auth.currentUser);
      }),
    checkEmailVerified: () =>
      wrap(async () => {
        const user = auth.currentUser;
        if (!user) return null;
        await reload(user);
        return user.emailVerified ? user.getIdToken(true) : null;
      }),
    signOut: () => signOut(auth),
  };
}
